<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import PlayoutSidebar from '../../components/TVPlayout/PlayoutSidebar.vue'
import PlayoutHeader from '../../components/TVPlayout/PlayoutHeader.vue'
import PlayoutRetainContents from '../../components/TVPlayout/PlayoutRetainContents.vue'
import PlayoutTable from '../../components/TVPlayout/PlayoutTable.vue';
import { mediaPulseModel } from '@/components/Composable/mediaPulse'
import StreamsWidget from '../../components/ui/StreamsWidget.vue';
import AlertModal from '@/components/modals/AlertModal.vue';
import PlayoutSetSchedule from '../../components/TVPlayout/PlayoutSetSchedule.vue';
import FormButton from '../../components/Atoms/FormButton.vue';
import { useNotification } from "@kyvg/vue3-notification";
import { useRouter } from 'vue-router';
import { ref, computed, watch, onUnmounted, onMounted, getCurrentInstance } from 'vue'
import Alert from '@/components/Atoms/Alert.vue';
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'

const store = useStore()
const route = useRoute()
const router = useRouter()
const { notify } = useNotification()
const instance = getCurrentInstance();
const root = instance.proxy.$root;

// Media Pulse 
const { subscribeMediaPulse, unsubscribeMediaPulse, mediaPulse } = mediaPulseModel()

const scopeAlive = ref(true)

const playout = computed(() => store.getters['Streams/tvPlayout'])
const playoutConfig = computed(() => store.getters['Playout/playoutConfig'])
const playoutDuration = computed(() => store.getters['Playout/playoutDuration'])
const playoutMaxDurationLimit = computed(() => store.getters['User/playoutMaxDurationLimit'])
const tvPlayoutLineUpDuration = computed(() => store.getters['User/tvPlayoutLineUpDuration'])
const isPlayoutInThePast = computed(() => store.getters['Playout/isPlayoutInThePast'])

onMounted( async () => {
	await store.dispatch('Streams/getStreams')
	subscribeMediaPulse(playout.value)
	try {
		await store.dispatch('Playout/getPlayoutConfig', route?.params?.tvId)
	} catch (err) {
		console.log(err)
		notify({
			group: 'error',
			text: 'Failed to load TV Playout data',
		})
		await router.push({ name: "ChannelList" });
	}
})
onUnmounted(() => {
	unsubscribeMediaPulse()
	scopeAlive.value = false
	store.dispatch('Playout/clear')
})
const addToPlayout = (stream) =>  {
	const durationLimit = playoutMaxDurationLimit.value * 1000 * 60 * 60
	const streamDuration = stream ? stream.mediaInfo?.duration * 1000 : 600000
	playoutConfig.value?.mode === 'schedule' 
		? store.dispatch('Playout/addItem', stream)
		: playoutDuration.value + streamDuration >= durationLimit
			? showModal('modal-overload-duration')
			: store.dispatch('Playout/addItem', stream)
}

const setSchedule = () => {
	if (playout.value.enabled && !mediaPulse.value) {
		subscribeMediaPulse(playout.value)
	}
}
</script>

<template>
	<div class="wrapper grid md:grid-cols-[292px_1fr] grid-cols-1 w-full min-h-screen ">
		<PlayoutSidebar :mediaPulse="mediaPulse" @enable-stream="setSchedule" />
		<div class="grid grid-cols-1 grid-rows-[auto_auto] md:grid-cols-[minmax(0,_1fr)_280px] md:grid-rows-[100px_1fr] gap-x-4 !px-4 md:!px-0 container detail-shell">
			<PlayoutHeader class="!hidden md:!flex md:col-span-2 md:order-1" />
			<main class="md:max-w-[calc(100%-10px)] md:pl-4 order-2">
				<PlayoutRetainContents />
				<Alert v-if="isPlayoutInThePast" type="warning" text="The selected start time is in the past. If your playlist duration is shorter than the elapsed time, the playout may already be finished." />
				<PlayoutTable :mediaPulse="mediaPulse" />
			</main>
			<aside class="!mt-4 md:!mt-0 md:pr-4 flex md:block justify-end order-1 md:order-3 items-center">
				<h3 class="mr-auto font-medium text-lg md:hidden">Schedule</h3>
				<PlayoutSetSchedule class="md:!mt-4 !mr-2 md:mr-0" @setSchedule="setSchedule" />
				<div id="#streams-widget-wrapper">
					<StreamsWidget placement="body" @add-stream="addToPlayout" />
				</div>
			</aside>
		</div>
		<alert-modal modalId="modal-overload-duration" message="Unable to add Video to Timetable"
			buttonAlignment="text-right" okText="Ok, got it" body-class="text-pretty">
			This video exceeds the duration limit of {{ tvPlayoutLineUpDuration }} hours. To increase the limit, contact <FormButton type="link" onclick="Intercom('show')" customClasses="inline underline text-white">sales team</FormButton>.
		</alert-modal>
	</div>
</template>

<style scoped></style>
